import { createStub } from './new-relic-stub';
import CircularJSON from 'circular-json';

import WixSDK from 'shared/utils/wix-sdk';


const logger = {
  debug: console.info.bind(console), // eslint-disable-line no-console
  error: console.error.bind(console), // eslint-disable-line no-console
};

const serializers = [
  {
    test: _.isError,
    serialize: error => JSON.stringify({
      stack: error.stack,
      message: error.message,
      name: error.name,
      details: error.details,
    }),
  },
  {
    test: _.constant(true),
    serialize: CircularJSON.stringify.bind(CircularJSON),
  },
];

function prettifyName(name) {
  return _.chain(name)
    .replace(/-/g, ' ')
    .split('/')
    .compact()
    .map(_.capitalize)
    .join(' → ')
    .value();
}

export default class NewRelic {
  constructor(newrelic, host) {
    this._host = host || location.host;
    this._newrelic = newrelic || createStub();
    this.init();
  }

  init() {
    window.addEventListener('hashchange', () => {
      this._updatePageData(global.location.pathname);
      this.finished();
    });

    this._newrelic.setCustomAttribute('sessionId', `s-${Date.now()}`);
    this._newrelic.setCustomAttribute('uid', WixSDK.Utils.getUid());
    this._newrelic.setCustomAttribute('instanceId', WixSDK.Utils.getInstanceId());
    this._newrelic.setCustomAttribute('biToken', WixSDK.Utils.getInstanceValue('biToken'));

    WixSDK.Utils.getMetaSiteId()
      .then(metaSiteId => {
        this._newrelic.setCustomAttribute('metaSiteId', metaSiteId);
      });
  }

  addPageAction(name, attributes) {
    logger.debug(`NewRelic page action: '${name}'`, attributes);
    this._newrelic.addPageAction(...arguments);
  }

  addToTrace(options) {
    logger.debug(`NewRelic trace: '${options.name}'`, options);
    this._newrelic.addToTrace(...arguments);
  }

  trackEvent(name, attributes) {
    if ('type' in attributes) {
      throw new Error(`Event '${name}' should not have type`);
    }

    this.addPageAction(`${name}`, attributes);
    this.addToTrace(_.assign({ name }, attributes));
  }

  logError(options = {}) {
    if (_.isError(options)) {
      return this.logError({ error: options });
    }

    if (_.isString(options)) {
      return this.logError({ message: options });
    }

    if (!options) {
      return;
    }

    const error = options.error || this._createError({ message: options.message, context: this.logError });
    const message = options.message || error.message;
    const details = _.assign({}, error.details, options.details);
    const type = options.type || 'custom';
    const additionalRootFields = _.omit(options, 'error', 'message', 'details', 'type');

    const errorInfo = {
      ...additionalRootFields,
      message,
      type,
      errorMessage: error.message,
      errorStack: error.stack,
      errorName: error.name,
      errorCode: error.code,
      errorId: error.id,
      details: _.mapValues(details, detail => this._serializeError(detail)),
    };

    this.addPageAction('error', errorInfo);
    this._noticeError(error);
  }

  _serializeError(arg) {
    const serializer = _.find(serializers, _serializer => _serializer.test(arg));
    return serializer.serialize(arg);
  }

  _noticeError(error) {
    logger.debug('Log error', error);
    this._newrelic.noticeError(error);
  }

  finished() {
    this._newrelic.finished();
  }

  setPageName(name) {
    this._newrelic.setPageViewName(name, this._host);
  }

  setCustomAttribute(key, value) {
    this._newrelic.setCustomAttribute(key, value);
  }

  _updatePageData(pathname) {
    this._newrelic.setCustomAttribute('stateName', pathname);
    this.setPageName(prettifyName(pathname));
  }

  _createError({ message = 'general-error', context = this._createError }) {
    try {
      throw new Error(message);
    } catch (ex) {
      if (Error.captureStackTrace) {
        // second param - local context. Will not be displayed in stack
        Error.captureStackTrace(ex, context);
      }
      return ex;
    }
  }
}



// WEBPACK FOOTER //
// ./src/shared/analytics/new-relic/new-relic.js